import { PaymentService, PaymentDueAccountVM } from "@propertelligent/client-api";
import { useState, useEffect } from "react";
import TextButton from "../../../newComponents/buttons/text-button";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import EmptyState from "../../../newComponents/emptystate";
import SimpleSpinner from "../../loader";
import { GetImages } from "../../../utils/GetImages";
import apiHelper from "../../../utils/apiHelper";
import DateHelper from "../../../utils/DateHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import "../platform-portal.css";

const NeedsReconciliation = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rows, setRows] = useState<PaymentDueAccountVM[]>([]);
  const [clearingRow, setClearingRow] = useState<PaymentDueAccountVM | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    const response = await apiHelper(
      PaymentService.getApiPaymentDueAccountPlatformNeedsReconciliation,
      { showNotification: false },
    );

    const success = response?.data?.success;
    setLoadError(!response || Boolean(response.errors) || Boolean(response.statusCode));
    setRows(Array.isArray(success) ? success : success ? [success] : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClearFlag = async () => {
    if (!clearingRow?.id) {
      return;
    }

    setIsClearing(true);
    // showNotification stays false here: the backend returns HTTP 200 with Success=false (not a thrown
    // error) when the row is already cleared/gone or still has a pending reservation -- apiHelper's own
    // success toast fires on any non-throwing call, so it would misreport a refused clear as successful.
    const response = await apiHelper(
      PaymentService.postApiPaymentDueAccountPlatformClearReconciliationFlag,
      { showNotification: false },
      Number(clearingRow.id),
    );

    setIsClearing(false);

    if (response?.data?.success) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Success,
        title: "Success",
        message: "Reconciliation flag cleared",
      });
      setClearingRow(null);
      fetchData();
      return;
    }

    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      title: "Failure",
      message:
        "Unable to clear reconciliation flag — it may already be cleared or a transfer is still pending.",
    });
  };

  return (
    <div className="platform-portal-page">
      <div className="top-listing-header-top d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <h2 className="H2">Needs Reconciliation</h2>
      </div>

      {loading ? (
        <div className="platform-loadmore">
          <SimpleSpinner />
        </div>
      ) : loadError ? (
        <EmptyState
          icon={GetImages.emptyStateData}
          heading="Unable to load flagged payments"
          isButton={true}
          buttonLabel="Retry"
          onButtonClick={fetchData}
          isBorder={false}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={GetImages.emptyStateData}
          heading="No flagged payments"
          isButton={false}
          isBorder={false}
        />
      ) : (
        <div className="platform-table-wrapper">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.referenceNumber || `#${row.id}`}</td>
                  <td>{row.description || "—"}</td>
                  <td>{row.formatted?.Amount || "—"}</td>
                  <td>
                    {row.dueDate ? DateHelper.convertUTCtoDateTimeSimple(row.dueDate, true) : "—"}
                  </td>
                  <td>
                    {row.reconciliationNote || "—"}
                    {row.autoPayTransferPending && (
                      <div className="sub_text">A transfer is still pending — cannot clear yet</div>
                    )}
                  </td>
                  <td>
                    <TextButton label="Clear Flag" onClick={() => setClearingRow(row)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmationPopup
        isOpen={Boolean(clearingRow)}
        title="Clear Reconciliation Flag"
        message={`Are you sure you want to clear the reconciliation flag for "${
          clearingRow?.referenceNumber || `#${clearingRow?.id}`
        }" (${clearingRow?.formatted?.Amount || "—"})? Only clear this once the underlying issue has been manually resolved.`}
        primaryButtonLabel="Clear Flag"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleClearFlag}
        onSecondaryAction={() => setClearingRow(null)}
        onClose={() => setClearingRow(null)}
        isPrimaryDestructive={false}
        isLoading={isClearing}
        loadingLabel="Clearing..."
      />
    </div>
  );
};

export default NeedsReconciliation;
